import { useState, useEffect } from "react";

import { Link, Typography } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// Chart Library
import { BarChart } from "@mui/x-charts/BarChart";
import { addLabels, createTableData, createPaginationData, fetchChartData, fetchTableData } from "../services/api";

// Table Library
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";

// Chip
import Chip from "@mui/material/Chip";

const config = {
    height: 350,
    margin: { left: 0 },
    yAxis: [
        {
            width: 50,
        },
    ],
};

function TestCaseTable() {
    const [fetching, setFetching] = useState(true);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (_, newPage) => {
        setFetching(true);
        initialTableData(newPage);
        setTimeout(() => {
            scrollToBottom();
        }, 100);
    };

    useEffect(() => {
        initialTableData();
    }, []);

    function scrollToBottom() {
        window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "instant", // or "auto"
        });
    }

    function capitalizeFirstLetter(val) {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

    function initialTableData(page) {
        fetchTableData(page)
            .then((data) => {
                setFetching(false);
                if (!!data.data)
                    setRows(
                        data.data.map((d) =>
                            createTableData(d.test_id, new Date(d.date_time).toString(), d.user_input, d.bot_response, d.expected_response, d.status)
                        )
                    );

                if (!!data.pagination) {
                    const pagination = createPaginationData(
                        data.pagination.page,
                        data.pagination.pageSize,
                        data.pagination.totalRows,
                        data.pagination.totalPages
                    );
                    setRowsPerPage(pagination.pageSize);
                    setTotalRows(pagination.rows);
                }
            })
            .catch((err) => {
                console.error(err);
                setFetching(false);
                setRows([]);
            });
    }

    return (
        <>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" className="fw-bold">
                                Test ID
                            </TableCell>
                            <TableCell align="center" className="fw-bold">
                                Date Time
                            </TableCell>
                            <TableCell align="center" className="fw-bold">
                                User Input
                            </TableCell>
                            <TableCell align="center" className="fw-bold">
                                Bot Response
                            </TableCell>
                            <TableCell align="center" className="fw-bold">
                                Expected Response
                            </TableCell>
                            <TableCell align="center" className="fw-bold">
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fetching || !rows.length ? (
                            <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                <TableCell colSpan={6}>
                                    <div className="d-flex align-items-center justify-content-center gap-3" style={{ minHeight: "25rem" }}>
                                        {!rows.length ? (
                                            <>
                                                <span>No Know Issues</span>
                                            </>
                                        ) : (
                                            <>
                                                <CircularProgress disableShrink size={16} />
                                                <span>Loading...</span>
                                            </>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                    <TableCell component="th" scope="row">
                                        {row.id}
                                    </TableCell>
                                    <TableCell>{row.datetime}</TableCell>
                                    <TableCell>{row.input}</TableCell>
                                    <TableCell>
                                        <div className="Table-Cell-Sm">{row.output}</div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="Table-Cell-Sm">{row.expected}</div>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip label={capitalizeFirstLetter(row.status)} color={row.status.toLowerCase() == "success" ? "success" : "error"} />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <div className="d-flex align-items-center justify-content-end py-3 bg-white border shadow-sm rounded-bottom-3" style={{ marginBottom: "-6px" }}>
                <Pagination count={Math.ceil(totalRows / rowsPerPage)} onChange={handleChangePage} />
            </div>
        </>
    );
}

function TestCasePage() {
    const now = new Date().toString();

    const [fetching, setFetching] = useState(true);
    const [data, setData] = useState([]);

    useEffect(() => {
        fetchChartData()
            .then((response) => {
                setFetching(false);
                setData(
                    response.data
                        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
                        .map((d) => ({
                            date: new Date(d.date).toDateString(),
                            success: d.success,
                            fail: d.failure,
                        }))
                );
            })
            .catch((err) => {
                console.error(err);
            });
    }, []);

    return (
        <div>
            <div className="PageBanner py-2 shadow-sm">
                <img src="/botnoi-logo-text.svg" alt="Botnoi Logo With Text" />
            </div>
            <div className="container">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Link href="/" underline="none">
                        Home
                    </Link>
                    <KeyboardArrowRightIcon />
                    <Typography>Chatbot Test Case Results</Typography>
                </div>
                <h1 className="text-center">Chatbot Test Case Results</h1>
                <div className="d-flex justify-content-center">
                    <Typography variant="caption" color="text.secondary">
                        Updated {now}
                    </Typography>
                </div>

                <div className="mb-5"></div>

                <div className="row">
                    <div className="col-12 col-lg-8 offset-lg-2">
                        <div className="d-flex align-items-center justify-content-center bg-white rounded-3 shadow-sm py-3 mb-5">
                            {fetching || !data?.length ? (
                                <div className="d-flex align-items-center justify-content-center gap-3" style={{ minHeight: "10rem" }}>
                                    {!data?.length ? (
                                        <>
                                            <span>No Know Issues</span>
                                        </>
                                    ) : (
                                        <>
                                            <CircularProgress disableShrink size={16} />
                                            <span>Loading...</span>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <BarChart
                                    dataset={data}
                                    series={addLabels([
                                        { dataKey: "success", stack: "s", color: "green" },
                                        { dataKey: "fail", stack: "s", color: "red" },
                                    ])}
                                    barLabel="value"
                                    xAxis={[{ dataKey: "date" }]}
                                    {...config}
                                />
                            )}
                        </div>

                        <div className="d-block p-3 border rounded-top-3 shadow-sm bg-white">
                            <h4 className="fw-bold mb-0">Chatbot Conversation Test Cases</h4>
                        </div>

                        <TestCaseTable />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TestCasePage;
